import { createReducer } from '@reduxjs/toolkit';
import { DemandOrderDataDto, DemandOrderDto } from '../../api/ClientApi';

import {
    saveDemandOrder,
    importDemandOrders,
    addOrderGoodsItemToDemandOrder,
    removeOrderGoodsItemFromDemandOrder,
    addOrUpdateDemandOrder,
} from '../actions/demandOrderActions';
import { cancelDemandOrder, closeDemandOrder } from '../actions/orderActions';

export interface DemandOrdersState {
    demandOrders: DemandOrderDto[] | null;
    demandOrdersInfoDate: Date | null;
    demandOrdersFetchActive: boolean;
}

export const demandOrdersReducer = createReducer<DemandOrdersState>(
    { demandOrders: null, demandOrdersInfoDate: null, demandOrdersFetchActive: false },
    (builder) => {
        builder
            .addCase(addOrderGoodsItemToDemandOrder, (state: DemandOrdersState, action: any) => {
                const { demandOrderId, demandOrderGoodsItem } = action.payload;

                if (!demandOrderGoodsItem || !state.demandOrders) {
                    return;
                }

                const demandOrderIndex = state.demandOrders?.findIndex((o) => o.id === demandOrderId);
                const demandOrderGoodsItemId = demandOrderGoodsItem.id;

                if (demandOrderIndex !== undefined && demandOrderIndex >= 0) {
                    const oldDemandOrder = state.demandOrders[demandOrderIndex];

                    const oldDemandOrderGoodsItemIndex = oldDemandOrder.orderGoodsItems?.findIndex(
                        (ogi) => ogi.id === demandOrderGoodsItemId,
                    );

                    if (oldDemandOrderGoodsItemIndex !== undefined && oldDemandOrderGoodsItemIndex >= 0) {
                        oldDemandOrder.orderGoodsItems?.splice(oldDemandOrderGoodsItemIndex, 1, demandOrderGoodsItem);
                    } else {
                        if (!oldDemandOrder.orderGoodsItems) {
                            oldDemandOrder.orderGoodsItems = [demandOrderGoodsItem];
                        } else {
                            oldDemandOrder.orderGoodsItems.unshift(demandOrderGoodsItem);
                        }
                    }

                    state.demandOrders.splice(demandOrderIndex, 1, oldDemandOrder);
                }
            })
            .addCase(removeOrderGoodsItemFromDemandOrder, (state: DemandOrdersState, action: any) => {
                const { demandOrderId, demandOrderGoodsItemId } = action.payload;

                if (!demandOrderGoodsItemId || demandOrderGoodsItemId <= 0 || !state.demandOrders) {
                    return;
                }

                const demandOrderIndex = state.demandOrders?.findIndex((o) => o.id === demandOrderId);

                if (demandOrderIndex !== undefined && demandOrderIndex >= 0) {
                    const oldDemandOrder = state.demandOrders[demandOrderIndex];

                    const oldDemandOrderGoodsItemIndex = oldDemandOrder.orderGoodsItems?.findIndex(
                        (ogi) => ogi.id === demandOrderGoodsItemId,
                    );

                    if (oldDemandOrderGoodsItemIndex !== undefined && oldDemandOrderGoodsItemIndex >= 0) {
                        oldDemandOrder.orderGoodsItems?.splice(oldDemandOrderGoodsItemIndex, 1);

                        state.demandOrders.splice(demandOrderIndex, 1, oldDemandOrder);
                    }
                }
            })
            .addCase(saveDemandOrder.fulfilled, (state: DemandOrdersState, action: any) => {
                if (state.demandOrders === null) {
                    return;
                }

                const payload: DemandOrderDto = action.payload;

                const demandOrder: DemandOrderDto = new DemandOrderDto();
                demandOrder.init(payload);

                const demandOrderIndex = state.demandOrders!.findIndex((a) => {
                    return a.id === demandOrder.id;
                });

                if (demandOrderIndex >= 0) {
                    // update demandOrder
                    state.demandOrders.splice(demandOrderIndex, 1, demandOrder);
                } else {
                    // add demandOrder
                    state.demandOrders.unshift(demandOrder);
                }
            })
            .addCase(cancelDemandOrder.fulfilled, (state: DemandOrdersState, action: any) => {
                if (state.demandOrders === null) {
                    return;
                }

                const payload: DemandOrderDto = action.payload;

                const order: DemandOrderDto = new DemandOrderDto();
                order.init(payload);

                const orderIndex = state.demandOrders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.demandOrders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.demandOrders.unshift(order);
                }
            })
            .addCase(closeDemandOrder.fulfilled, (state: DemandOrdersState, action: any) => {
                if (state.demandOrders === null) {
                    return;
                }

                const payload: DemandOrderDto = action.payload;

                const order: DemandOrderDto = new DemandOrderDto();
                order.init(payload);

                const orderIndex = state.demandOrders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.demandOrders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.demandOrders.unshift(order);
                }
            })
            .addCase(addOrUpdateDemandOrder, (state: DemandOrdersState, action: any) => {
                const newDemandOrder = action.payload;

                const oldDemandOrderIndex = state.demandOrders?.findIndex((o) => o.id === newDemandOrder.id);

                if (oldDemandOrderIndex !== undefined && oldDemandOrderIndex >= 0) {
                    state.demandOrders?.splice(oldDemandOrderIndex, 1, newDemandOrder);
                } else {
                    if (!state.demandOrders) {
                        state.demandOrders = [newDemandOrder];
                    } else {
                        state.demandOrders.unshift(newDemandOrder);
                    }
                }
            })
            .addCase(importDemandOrders.fulfilled, (state: DemandOrdersState, action: any) => {
                const result: DemandOrderDataDto = action.payload;

                const newDemandOrders = result.orders;

                state.demandOrders = newDemandOrders?.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)) ?? null;
                state.demandOrdersInfoDate = result.infoDate ?? null;

                state.demandOrdersFetchActive = false;
            })
            .addCase(importDemandOrders.pending, (state: DemandOrdersState, action: any) => {
                state.demandOrdersFetchActive = true;
            })
            .addCase(importDemandOrders.rejected, (state: DemandOrdersState, action: any) => {
                state.demandOrdersFetchActive = false;
            });
    },
);
